import { useMemo, useRef } from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import { isNull, isUndefined } from 'lodash'
import sortBy from 'lodash/sortBy'
import moment from 'moment'

import { observer } from 'mobx-react'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import CustomFields from 'modules/custom_resources/store/collections/customFields/fields'
import CustomValues from 'modules/custom_resources/store/collections/customFields/values'
import type CustomField from 'modules/custom_resources/store/models/customFields/Field'
import type { ICustomField } from 'modules/custom_resources/store/models/customFields/Field'
import { ChoiceOption } from 'modules/custom_resources/store/models/customFields/Field'
import QuestionsWithoutCategory from 'modules/enps/pages/AnswersPage/Questions/QuestionsWithoutCategory'
import coerceValueFromType from 'modules/surveys/lib/coerceValueFromType'
import type QuestionGroup from 'modules/surveys/store/models/QuestionGroup'
import type { IQuestionGroup } from 'modules/surveys/store/models/QuestionGroup'

function missingMandatory(forms: Form[]): boolean {
  return Boolean(
    forms
      .filter((form) => Boolean(form.get('mandatory').value))
      .find((form) => {
        const value = form.get('value').value

        return isNull(value) || isUndefined(value) || value === ''
      })
  )
}

function isFormDirty(forms: Form[]): boolean {
  return forms.some((form: Form) => form.isDirty)
}

function clearForms(forms: Form[]) {
  forms.map((form) => form.cleanAll())
}

interface Props {
  questionGroup: QuestionGroup
  onDraftSaved: () => Promise<void>
  canEdit: boolean
}

type ValueType = string | boolean | number | null

interface ResponseData {
  surveys_question_group_id: number
  answers: Record<string, unknown>
  is_draft: boolean
}

type QuestionPayload = IQuestionGroup['questions'][0]
type MappedQuestionPayload = QuestionPayload | (ICustomField & { position: number })

function ResponseActiveQuestions({ questionGroup, onDraftSaved, canEdit }: Props) {
  const questionsWithoutCategory = questionGroup.withoutCategory

  const createForm = (field: CustomField, value: ValueType) => {
    const { id, field_type, label, min_value, max_value, required } = field.attributes

    const values = {
      field_id: id,
      field_type: field_type,
      mandatory: required,
      name: label,
      min_value: min_value,
      max_value: max_value,
      value,
    }

    return new Form(values, {
      field_id: 'number',
      field_type: 'string',
      mandatory: 'boolean',
      name: 'string',
      min_value: 'number',
      max_value: 'number',
      value: 'string',
    })
  }

  const onFormSubmittedOrDrafted = () => {
    clearForms(formsWithoutCategory)
  }

  // Questions without category
  const customFieldsWithoutCategory = useRef(
    new CustomFields(
      questionsWithoutCategory.map((payload: QuestionPayload) => {
        if (!payload.custom_field) return payload

        return { ...payload.custom_field, position: payload.position }
      }) as unknown as CustomField[],
      {
        model_ref: 'surveys-question_group',
        instance_id: questionGroup.attributes.id,
      }
    )
  ).current

  const customValuesWithoutCategory = useRef(
    new CustomValues(questionGroup.attributes.answers, {
      model_ref: 'QuestionGroup',
      instance_id: questionGroup.attributes.id,
    })
  ).current

  const optionsWithoutCategory = useMemo(() => {
    return customFieldsWithoutCategory.models.reduce<Record<string, ChoiceOption[]>>((memo, cf) => {
      memo[cf.attributes.id] = cf.choiceOptions.filter((opt) => opt.isActive)

      return memo
    }, {})
  }, [customFieldsWithoutCategory])

  const formsWithoutCategory = useRef(
    sortBy(customFieldsWithoutCategory.models, (cf) => cf.attributes.position).map((field) => {
      const answer = customValuesWithoutCategory.find({
        field_id: field.attributes.id,
      })

      const value = answer ? coerceValueFromType(field, answer.attributes.value) : ''

      return createForm(field, value)
    })
  ).current

  const computeData = ({ isDraft = false } = {}): ResponseData => {
    const data: ResponseData = {
      surveys_question_group_id: questionGroup.attributes.id,
      answers: {},
      is_draft: isDraft,
    }

    formsWithoutCategory.forEach((form: Form) => {
      const fieldId = form.get('field_id').value
      const value = form.get('value').value

      if (value !== undefined) data.answers[fieldId] = value
    })
    return data
  }

  const { running: runningSubmit, performAction: onSubmitAnswer } = useAsyncAction(
    async () => {
      const data = computeData()

      const { success, value } = await asyncAction(() => questionGroup.rpc('create_values', data))

      if (success) {
        const data = {
          answered_at: dateToString(moment(), 'backend'),
        }
        questionGroup.set(data)
        onFormSubmittedOrDrafted()
        onDraftSaved && onDraftSaved()
      }

      track(trackingEvents.enps.submitEnpsAnswer, {
        is_succesfull: success,
        error_code: !success && JSON.stringify(value),
        score: Object.values(data.answers)[0] as number,
        has_answer: Object.values(data.answers)[1] as string,
      })
    },
    {
      successMessage: i18n.t('enps.form.answer.save_success'),
    }
  )

  return (
    <>
      {!!formsWithoutCategory.length && (
        <Box gap="s48" paddingX="s24">
          {formsWithoutCategory.map((form: Form, index: number) => {
            const field = customFieldsWithoutCategory.get(Number(form.get('field_id').value))

            if (!field) {
              return null
            }

            return (
              <QuestionsWithoutCategory
                canEdit={canEdit}
                key={index}
                form={form}
                options={optionsWithoutCategory}
                field={field}
              />
            )
          })}
        </Box>
      )}
      {canEdit && (
        <Box justifyContent="flexEnd" flexDirection="row" flexGrow paddingX="s24">
          <Button
            onClick={onSubmitAnswer}
            disabled={
              runningSubmit ||
              !isFormDirty(formsWithoutCategory) ||
              missingMandatory(formsWithoutCategory)
            }
          >
            {i18n.t('enps.form.answer.save')}
          </Button>
        </Box>
      )}
    </>
  )
}

export default observer(ResponseActiveQuestions)
